/**
 * ParentQuickReadButton Component
 *
 * A call-to-action button for parents to quickly start reading with their child.
 * Uses the recommendation system to find the best next story for the selected child.
 */

import { Button, Loader, Tooltip } from '@mantine/core';
import { IconPlayerPlay, IconSparkles } from '@tabler/icons-react';
import { useParentQuickRead } from '../hooks/useParentQuickRead';
import { capitalizeName } from '@/utils/name';

interface ParentQuickReadButtonProps {
  /** The student ID of the child to get recommendations for */
  studentId: string | null;
  /** Name of the child (for display in notifications and button text) */
  childName?: string;
  /** Button size variant */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Whether to show as full width */
  fullWidth?: boolean;
  /** Button variant */
  variant?: 'filled' | 'light' | 'outline' | 'gradient';
  /** Custom class name */
  className?: string;
}

export function ParentQuickReadButton({
  studentId,
  childName,
  size = 'md',
  fullWidth = false,
  variant = 'gradient',
  className,
}: ParentQuickReadButtonProps) {
  const displayName = capitalizeName(childName);
  const { startQuickRead, state, isDisabled } = useParentQuickRead({
    studentId,
    childName: displayName,
  });

  const buttonContent = () => {
    switch (state) {
      case 'loading':
        return (
          <>
            <Loader size={16} color="white" />
            Finding story...
          </>
        );
      case 'generating':
        return (
          <>
            <Loader size={16} color="white" />
            Creating story...
          </>
        );
      default:
        return (
          <>
            <IconSparkles size={18} />
            {displayName ? `Read with ${displayName}` : 'Quick Read'}
          </>
        );
    }
  };

  const tooltipLabel = displayName
    ? `Start reading the best story for ${displayName} right now`
    : 'Select a child to start reading';

  return (
    <Tooltip
      label={tooltipLabel}
      position="bottom"
      withArrow
      disabled={state !== 'idle'}
    >
      <Button
        onClick={startQuickRead}
        disabled={isDisabled}
        size={size}
        fullWidth={fullWidth}
        variant={variant}
        gradient={variant === 'gradient' ? { from: 'orange', to: 'pink', deg: 90 } : undefined}
        leftSection={state === 'idle' ? <IconPlayerPlay size={18} /> : null}
        className={className}
        styles={{
          root: {
            fontWeight: 600,
          },
        }}
      >
        {buttonContent()}
      </Button>
    </Tooltip>
  );
}
